import React from 'react';
import { Box, Typography } from '@mui/material';

import { LOGIN } from './tokens';

/**
 * A unique id per rendered icon.
 *
 * The page renders this icon more than once (the panel, the card, and a
 * phone-only copy of the panel that is `display: none` on desktop). With a
 * fixed gradient id every copy pointed at the *first* definition - and when
 * that one sat inside a hidden subtree the gradient rendered nowhere, leaving
 * an empty tile on the card. React's `useId` contains colons, which are not
 * safe inside `url(#...)`, so they are stripped.
 */
const useSvgId = (prefix: string) => `${prefix}${React.useId().replace(/:/g, '')}`;

const STROKES = {
  onDark: [LOGIN.cyan, LOGIN.blue],
  // On the white card the light cyan washes out; a deeper pair keeps the mark legible.
  onLight: ['#0EA5E9', '#1D4ED8'],
} as const;

/** Cloud with a pulse line - "we watch the cloud". Decorative; the wordmark carries the name. */
export const CloudPulseIcon: React.FC<{ size?: number; tone?: keyof typeof STROKES }> = ({
  size = 40,
  tone = 'onDark',
}) => {
  const gradient = useSvgId('cloudGrad');
  const [from, to] = STROKES[tone];
  const stroke = tone === 'onLight' ? 3 : 2.6;
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={gradient} x1="6" y1="10" x2="42" y2="40" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={from} />
          <stop offset="1" stopColor={to} />
        </linearGradient>
      </defs>
      <path
        d="M14 36h21a8 8 0 0 0 1.4-15.9A11 11 0 0 0 15.3 17 9.5 9.5 0 0 0 14 36Z"
        fill="none" stroke={`url(#${gradient})`} strokeWidth={stroke} strokeLinejoin="round"
      />
      <path
        d="M13 28h6l2.5-5 4 9 2.5-4H35"
        fill="none" stroke={`url(#${gradient})`} strokeWidth={stroke}
        strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
};

interface WordmarkProps {
  /** `dark` sits on the navy panel, `light` on the white card. */
  tone: 'dark' | 'light';
  size?: 'md' | 'lg';
  component?: React.ElementType;
}

export const Wordmark: React.FC<WordmarkProps> = ({ tone, size = 'md', component = 'span' }) => (
  <Typography
    component={component}
    sx={{
      fontFamily: LOGIN.font,
      fontWeight: 800,
      letterSpacing: '-0.025em',
      lineHeight: 1.1,
      fontSize: size === 'lg' ? { xs: '1.95rem', sm: '2.2rem' } : '1.4rem',
      color: tone === 'dark' ? '#FFFFFF' : LOGIN.cardInk,
      m: 0,
    }}
  >
    AWS{' '}
    <Box component="span" sx={{ color: tone === 'dark' ? LOGIN.cyan : LOGIN.blue }}>
      Monitor
    </Box>
  </Typography>
);

export { useSvgId };
